/**
 * Direct messages — one screen, both nav branches (§7.1).
 *
 * A renter holding `read_dm` sees exactly what the owner sees, and a renter
 * holding `dm` can reply exactly as the owner can. That is not generosity, it is
 * the product: a rental that cannot answer the persona's enquiries is not the
 * thing being rented. Nothing on this screen branches on `scope`; everything
 * branches on the server's capability set (§7.4 rule 1).
 *
 * Two capabilities, deliberately distinct:
 *
 *   `read_dm` — see the inbox and threads, and refresh them.
 *   `dm`      — send into a thread.
 *
 * An owner can grant the second without the first only in the sense that neither
 * implies the other; in practice granting `dm` alone leaves a composer with
 * nothing to aim at, which is why the reply box lives inside a thread.
 *
 * The rule that shapes the whole layout is the no-cold-DM rule. A bot may only
 * send into a conversation the counterparty opened, so `initiated_by_counterparty`
 * is rendered on every thread rather than hidden — a composer that silently
 * refuses is indistinguishable from one that is broken. Where the rule forbids a
 * reply, the reason is shown in place of the box, before anything is typed.
 */

import { useState } from 'react';
import { DM_CONSTRAINTS } from '@pm/shared';

import { useApi, useMutation } from '../../hooks/useApi';
import { useBotOutlet } from '../botOutlet';
import type { Action, DmConversation, DmMessage, DmSyncResult, Page } from '../../api/types';
import { Card, Badge, Loading } from '../../components/ui';
import { EmptyState } from '../../components/EmptyState';
import { ErrorView, InlineError } from '../../components/ErrorView';
import { Gate } from '../../components/Gate';
import { localDateTime } from '../../lib/format';
import { routes } from '../../api/routes';

/**
 * Why this thread cannot be replied into, or null when it can.
 *
 * Mirrors `assertSendable` in the API, minus the volume caps — those depend on
 * counts the client does not hold, and are reported by the server when a send is
 * refused. Duplicating the two *permanent* reasons here is worth it because they
 * are knowable from data already on screen, and telling someone after they have
 * written a reply is the worst moment to tell them.
 */
function replyBlockedReason(c: DmConversation): string | null {
  if (c.closed_at) {
    return c.closed_reason
      ? `This thread was closed: ${c.closed_reason}`
      : 'This thread was closed.';
  }
  if (DM_CONSTRAINTS.requireCounterpartyInitiated && !c.initiated_by_counterparty) {
    return (
      'This platform only sends into conversations the other person started, and that ' +
      'has not been established for this thread. Nothing you send here would go out.'
    );
  }
  return null;
}

export function DirectMessages() {
  const { bot, caps } = useBotOutlet();
  const [openId, setOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const conversations = useApi<Page<DmConversation>>(routes.bots.dmConversations(bot.id));
  const sync = useMutation<Record<string, never>, DmSyncResult>(routes.bots.dmSync(bot.id));
  /*
   * Replies the bot has written and is holding. In `approve` mode these are the
   * point of the screen — a draft nobody can see is a draft nobody approves —
   * and they are polled because they appear without anyone doing anything here.
   */
  const drafts = useApi<Page<Action>>(routes.content.actions(bot.id), {
    query: { status: 'approving', limit: 50 },
    pollMs: 30_000,
  });

  if (conversations.initialLoading) return <Loading label="Loading conversations" />;
  if (conversations.problem) {
    return <ErrorView problem={conversations.problem} onRetry={conversations.reload} />;
  }

  const threads = conversations.data?.data ?? [];
  const open = threads.find((t) => t.id === openId) ?? null;

  return (
    <div className="dm">
      <header className="page-head">
        <h1>Direct messages</h1>
        <Gate caps={caps} capability="read_dm">
          {(disabled) => (
            <button
              type="button"
              className="btn"
              disabled={disabled || sync.pending}
              onClick={async () => {
                if ((await sync.run({})) !== null) conversations.reload();
              }}
            >
              {sync.pending ? 'Checking for new messages…' : 'Check for new messages'}
            </button>
          )}
        </Gate>
      </header>

      {/*
        Refreshing is not cosmetic here. A send is refused into any thread the
        platform has not seen, so an un-synced inbox is also an inbox that cannot
        be replied to — worth saying once rather than leaving someone to infer it
        from a refusal.
      */}
      {sync.data ? (
        <p className="muted" role="status">
          Found {sync.data.seen} thread(s); {sync.data.classified} newly classified by who
          spoke first.
        </p>
      ) : null}
      {sync.problem ? <InlineError problem={sync.problem} /> : null}

      {threads.length === 0 ? (
        <Card>
          <EmptyState
            heading="No conversations yet"
            body="Threads appear here once someone messages this account and you check for new messages. This bot cannot start a conversation itself."
          />
        </Card>
      ) : (
        <div className="dm-split">
          <Card title={`${threads.length} thread(s)`}>
            <ul className="dm-list">
              {threads.map((t) => {
                const blocked = replyBlockedReason(t);
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      className={`dm-thread${t.id === openId ? ' dm-thread-active' : ''}`}
                      onClick={() => {
                        setOpenId(t.id);
                        setDraft('');
                      }}
                    >
                      <span className="dm-handle">@{t.counterparty_handle || 'unknown'}</span>
                      <span className="muted">
                        {t.last_message_at ? localDateTime(t.last_message_at) : 'no messages yet'}
                      </span>
                      {/*
                        Both states are labelled. "Reply blocked" with no
                        counterpart would read as an error rather than as the
                        normal condition of a thread nobody opened.
                      */}
                      {blocked ? (
                        <Badge tone="warn">Reply blocked</Badge>
                      ) : (
                        <Badge tone="ok">Can reply</Badge>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>

          {open ? (
            <Thread
              key={open.id}
              botId={bot.id}
              conversation={open}
              draft={draft}
              setDraft={setDraft}
              caps={caps}
              pendingDraft={
                (drafts.data?.data ?? []).find(
                  (a) =>
                    a.type === 'dm' &&
                    (a.target_ref as { conversation_id?: string } | null)?.conversation_id ===
                      open.external_id,
                ) ?? null
              }
              onSent={() => {
                conversations.reload();
                drafts.reload();
              }}
            />
          ) : (
            <Card>
              <EmptyState heading="No thread selected" body="Pick a conversation to read it." />
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function Thread({
  botId,
  conversation,
  draft,
  setDraft,
  caps,
  pendingDraft,
  onSent,
}: {
  botId: string;
  conversation: DmConversation;
  draft: string;
  setDraft: (v: string) => void;
  caps: ReturnType<typeof useBotOutlet>['caps'];
  /** A reply the bot wrote and is holding for approval, if there is one. */
  pendingDraft: Action | null;
  onSent: () => void;
}) {
  const messages = useApi<Page<DmMessage>>(routes.bots.dmMessages(botId, conversation.id));
  // A DM is an Action like any other, so it goes through the action endpoint and
  // lands in the same queue and the same activity log. There is no separate send
  // path, which is what keeps the audit trail single.
  const send = useMutation<Record<string, unknown>, { id: string }>(
    routes.content.actions(botId),
    { idempotent: true },
  );

  const approve = useMutation<Record<string, never>, unknown>(
    () => routes.content.actionApproval(pendingDraft?.id ?? ''),
  );
  const discard = useMutation<Record<string, never>, unknown>(
    () => routes.content.action(pendingDraft?.id ?? ''),
    { method: 'DELETE' },
  );

  const blocked = replyBlockedReason(conversation);

  return (
    <Card title={`@${conversation.counterparty_handle || 'unknown'}`}>
      {messages.initialLoading ? <Loading label="Loading this thread" /> : null}
      {messages.problem ? (
        <ErrorView problem={messages.problem} onRetry={messages.reload} />
      ) : null}

      {messages.data ? (
        messages.data.data.length === 0 ? (
          <EmptyState
            heading="Nothing in this thread yet"
            body="Messages appear here after the next check."
          />
        ) : (
          <ol className="dm-messages">
            {messages.data.data.map((m) => (
              <li key={m.id} className={`dm-message dm-${m.direction}`}>
                <p className="dm-body">{m.body}</p>
                <p className="muted">
                  {m.direction === 'inbound' ? 'Received' : 'Sent'} {localDateTime(m.sent_at)}
                  {/*
                    Who directed an outbound message matters once a bot has been
                    rented: the owner should be able to see what was said in
                    their persona's name and by whom.
                  */}
                  {m.actor_role ? ` by the ${m.actor_role}` : ''}
                </p>
              </li>
            ))}
          </ol>
        )
      ) : null}

      {/*
        The bot has written something and is waiting. Shown above the composer
        rather than inside it: this is a decision to make, not a field to edit,
        and the two should not look the same.
      */}
      {pendingDraft ? (
        <div className="dm-draft" role="group" aria-label="Reply awaiting approval">
          <p className="dm-draft-label">
            <Badge tone="warn">Waiting for approval</Badge> The bot wrote this reply and has not
            sent it.
          </p>
          <p className="dm-body">{pendingDraft.body}</p>
          <div className="dm-draft-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={approve.pending}
              onClick={async () => {
                if ((await approve.run({})) !== null) onSent();
              }}
            >
              Send it
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={discard.pending}
              onClick={async () => {
                if ((await discard.run({})) !== null) onSent();
              }}
            >
              Discard
            </button>
          </div>
          {approve.problem ? <InlineError problem={approve.problem} /> : null}
          {discard.problem ? <InlineError problem={discard.problem} /> : null}
        </div>
      ) : null}

      {blocked ? (
        <p className="failure-reason" role="status">
          {blocked}
        </p>
      ) : (
        <Gate
          caps={caps}
          capability="dm"
          fallback={
            <p className="muted">
              This rental does not include sending direct messages, so there is no reply box.
            </p>
          }
        >
          {(disabled, reason) => (
            <div className="dm-composer">
              <label htmlFor="dm-reply">Reply</label>
              <textarea
                id="dm-reply"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={3}
                disabled={disabled}
              />
              <button
                type="button"
                className="btn btn-primary"
                disabled={disabled || send.pending || draft.trim() === ''}
                onClick={async () => {
                  const created = await send.run({
                    type: 'dm',
                    // The account the thread arrived on, not "the bot's first
                    // account": a bot may hold several and a reply must go out
                    // through the one the counterparty wrote to.
                    social_account_id: conversation.social_account_id,
                    body: draft,
                    asset_ids: [],
                    // The platform's own conversation id, which is what
                    // `assertSendable` looks the thread up by.
                    target_ref: { conversation_id: conversation.external_id },
                    scheduled_at: null,
                  });
                  if (created) {
                    setDraft('');
                    send.reset();
                    messages.reload();
                    onSent();
                  }
                }}
              >
                {send.pending ? 'Sending…' : 'Send'}
              </button>
              {reason ? null : (
                <p className="muted">
                  Capped at {DM_CONSTRAINTS.defaultPerConversationDailyCap} messages into this
                  thread per day, {DM_CONSTRAINTS.defaultDailyCap} across the bot.
                </p>
              )}
              {/*
                A refusal from the server is not a transport failure and is not
                retryable — a cold thread is cold on the next attempt too. It is
                rendered where the reply was typed rather than as a toast.
              */}
              {send.problem ? <InlineError problem={send.problem} /> : null}
            </div>
          )}
        </Gate>
      )}
    </Card>
  );
}
